import type { SmtpSettingsUpdate } from '@sentinel/shared';
import { Panel } from '@sentinel/ui';
import {
  EmailSettingsForm,
  EmailTestSection,
  QueryState,
  toast,
  useErrorMessage,
} from '@sentinel/web-core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { consoleEndpoints } from '@/api/endpoints';
import { keys } from '@/api/keys';
import { PlatformRetentionPanel } from './PlatformRetentionPanel';

/** Platform settings (platform owner): the outgoing mail server every organization without its
 *  own uses (ADR 0016), with a test e-mail and the outcome of the last delivery; the platform's
 *  retention run. */
export function PlatformSettingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const onError = (error: unknown) => toast('att', errorMessage(error));
  const settings = useQuery({ queryKey: keys.smtp, queryFn: consoleEndpoints.smtp });
  const save = useMutation({
    mutationFn: (update: SmtpSettingsUpdate) => consoleEndpoints.updateSmtp(update),
    onSuccess: (next) => {
      queryClient.setQueryData(keys.smtp, next);
      toast('ok', t('smtp.saved'));
    },
    onError,
  });
  const test = useMutation({
    mutationFn: (to: string) => consoleEndpoints.testSmtp(to),
    onSuccess: (result) => {
      toast(result.outcome === 'ok' ? 'ok' : 'att', t(`smtp.test.${result.outcome}`));
      void queryClient.invalidateQueries({ queryKey: keys.smtp });
    },
    onError,
  });
  return (
    <div className="stack">
      <Panel title={t('console.settings.mailTitle')}>
        <div className="stack">
          <p className="note">{t('console.settings.mailLead')}</p>
          <QueryState query={settings}>
            {(current) => (
              <div className="stack">
                <EmailSettingsForm
                  key={current.configured ? 'saved' : 'new'}
                  current={current}
                  busy={save.isPending}
                  onSave={(update) => save.mutate(update)}
                />
                <EmailTestSection
                  settings={current}
                  busy={test.isPending}
                  onTest={(to) => test.mutate(to)}
                />
              </div>
            )}
          </QueryState>
        </div>
      </Panel>
      <PlatformRetentionPanel />
    </div>
  );
}
